// filepath: components/entities/PropertyRenderer.tsx
import React, { useCallback } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { BaseEntityProps, EntityRowAction, LeasingPropertyDTO, MemberData } from '#types/types';
import { Building, Landmark, ExternalLink, UserMinus, Hash } from '@/components/ui/lucide-memo';
import { toIsoCountryCodes } from '#lib/country-codes';
import { toCountryFlag } from '#lib/leasing/brandDisplay';
import clsx from 'clsx';
import { UserSelector } from '../portal/UserSelector';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import styles from '../portal/Table.module.css';
import type { TableColumnDef } from '../portal/Table';

const DEFAULT_ROW_COLUMN_ORDER = ['property', 'owner', 'type', 'managers', 'units', 'occupancy', 'vacant', 'actions'] as const;
// Same flag glyph as everywhere else, but tolerant of free-form input (country name, address):
// the ISO resolution is the only thing this wrapper adds on top of the canonical helper.
const toResolvedCountryFlag = (rawCountry: string | null | undefined): string =>
  toCountryFlag(toIsoCountryCodes(rawCountry).at(0));

const normalizeCityCandidate = (value: string): string => {
  const cleaned = value
    .replace(/\b\d{4,6}(?:[-\s]\d{2,4})?\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned;
};

const extractCityFromAddress = (rawAddress: string | null | undefined): string => {
  const source = (rawAddress ?? '').trim();
  if (!source) return '';

  const parts = source
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0) return '';

  const candidates: string[] = [];
  if (parts.length >= 2) {
    candidates.push(parts[parts.length - 2]!);
  }
  candidates.push(parts[parts.length - 1]!);
  for (const part of parts) {
    if (!candidates.includes(part)) candidates.push(part);
  }

  for (const candidate of candidates) {
    if (toIsoCountryCodes(candidate).length > 0) continue;
    const normalized = normalizeCityCandidate(candidate);
    if (normalized.length > 1) return normalized;
  }

  return '';
};

const stopEventPropagation = (e: React.MouseEvent) => e.stopPropagation();

// Three shared objects instead of one literal per cell per render.
const CELL_JUSTIFY_STYLES: Record<'start' | 'center' | 'end', React.CSSProperties> = {
  start: { justifyContent: 'flex-start' },
  center: { justifyContent: 'center' },
  end: { justifyContent: 'flex-end' },
};

const resolveCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === 'actions' || column.align === 'right') return CELL_JUSTIFY_STYLES.end;
  if (column.align === 'center') return CELL_JUSTIFY_STYLES.center;
  return CELL_JUSTIFY_STYLES.start;
};

const PropertyManagerChip = React.memo(({
  manager,
  onContextMenu,
}: {
  manager: MemberData;
  onContextMenu: (e: React.MouseEvent, manager: MemberData) => void;
}) => {
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    onContextMenu(e, manager);
  }, [manager, onContextMenu]);

  return <UserSelector selectedMember={manager} onContextMenu={handleContextMenu} />;
});
PropertyManagerChip.displayName = 'PropertyManagerChip';

const PropertyThumbnail = React.memo(({ url, alt }: { url?: string | null; alt: string }) => (
  <div className="relative flex h-full w-full flex-none items-center justify-center overflow-hidden bg-white/5 text-white/20">
    {url ? (
      <img src={url} alt={alt} className="h-full w-full object-cover" loading="lazy" />
    ) : (
      <Landmark size={24} />
    )}
  </div>
));
PropertyThumbnail.displayName = 'PropertyThumbnail';

export interface PropertyRendererProps extends BaseEntityProps<LeasingPropertyDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingPropertyDTO>[];
}

export const PropertyRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = [],
}: PropertyRendererProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const data = useKernelStore((state) => state.inventory.Property.registry[entityId]) as LeasingPropertyDTO | undefined;
  const portfolio = useKernelStore((state) =>
    data?.portfolioId ? state.inventory.Portfolio.registry[data.portfolioId] : undefined
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  }, [onClick]);

  // The write replaces the whole leasingManagerIds array, so it must start from the registry
  // at click time: a DTO captured in the closure can be one socket update behind and would
  // silently drop a manager someone else just assigned.
  const readCurrentManagerIds = useCallback((): Set<string> => {
    const current = kernel.current?.getState().inventory.Property.registry[entityId] as LeasingPropertyDTO | undefined;
    return new Set([
      ...(current?.leasingManagerIds ?? []),
      ...((current?.leasingManagers ?? []).map((manager) => manager.id)),
    ]);
  }, [entityId, kernel]);

  const handleAddLeasingManager = useCallback((member: { id: string } | null) => {
    if (!member) return;
    const currentIds = readCurrentManagerIds();
    if (currentIds.has(member.id)) return;
    void kernel.current?.updateEntity('Property', entityId, {
      leasingManagerIds: [...currentIds, member.id],
    });
  }, [entityId, kernel, readCurrentManagerIds]);

  const handleRemoveLeasingManager = useCallback((managerId: string) => {
    const currentIds = readCurrentManagerIds();
    if (!currentIds.has(managerId)) return;
    const nextIds = Array.from(currentIds).filter((id) => id !== managerId);
    void kernel.current?.updateEntity('Property', entityId, {
      leasingManagerIds: nextIds,
    });
  }, [entityId, kernel, readCurrentManagerIds]);

  const handleManagerContextMenu = useCallback((e: React.MouseEvent, manager: MemberData) => {
    e.preventDefault();
    e.stopPropagation();
    const managerName = [manager.firstName, manager.lastName].filter(Boolean).join(' ').trim() || manager.email || '';
    const label = lang === 'fr'
      ? `Desaffecter ${managerName || 'ce gestionnaire'}`
      : `Unassign ${managerName || 'this manager'}`;
    kernel.current?.setMenuData({
      position: { x: e.clientX, y: e.clientY },
      actions: [
        {
          label,
          icon: <UserMinus size={14} className="text-white/70" />,
          onClick: () => handleRemoveLeasingManager(manager.id),
        },
      ],
    });
  }, [handleRemoveLeasingManager, kernel, lang]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  const thumbnailAssetId = data.thumbnailFileId || data.thumbnailFile?.id;
  const imageUrl = thumbnailAssetId ? `/api/inventory/assets/${thumbnailAssetId}` : null;
  const unitCount = typeof data.totalUnits === 'number'
    ? data.totalUnits
    : Array.isArray(data.unitIds)
      ? data.unitIds.length
      : 0;
  const displayUnitCount =
    typeof data.declaredUnitCount === 'number' && data.declaredUnitCount > 0
      ? data.declaredUnitCount
      : unitCount;
  const resolvedAddress = data.location?.resolvedAddress ?? null;
  const locationCity = typeof data.location?.city === 'string' ? data.location.city : '';
  const locationCountry =
    typeof data.location?.country === 'string'
      ? data.location.country
      : typeof data.location?.countryCode === 'string'
        ? data.location.countryCode
        : '';
  const cityLabel =
    (data.city || '').trim() ||
    locationCity.trim() ||
    extractCityFromAddress(resolvedAddress) ||
    extractCityFromAddress(data.address);
  const fallbackCityLabel = lang === 'fr' ? 'Ville inconnue' : 'Unknown city';
  const countryFlag =
    toResolvedCountryFlag(data.country) ||
    toResolvedCountryFlag(locationCountry) ||
    toResolvedCountryFlag(resolvedAddress) ||
    toResolvedCountryFlag(data.address);

  if (variant === 'chip') {
    return (
      <div
        className={clsx('flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80 hover:bg-white/10', className)}
        onClick={handleClick}
      >
        <Building size={12} />
        <span className="truncate">{data.name}</span>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={clsx(
          'flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:border-white/20 hover:bg-white/10',
          className
        )}
        onClick={handleClick}
      >
        <div className="h-[136px] w-full border-b border-white/10">
          <PropertyThumbnail url={imageUrl} alt={data.name || 'Property'} />
        </div>
        <div className="flex min-w-0 flex-1 items-center p-2.5">
          <div className="flex min-w-0 w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
              <div className="min-w-0 truncate text-sm font-medium text-white/90">{data.name}</div>
              <span className="flex-none text-[10px] text-white/25">•</span>
              <div className="flex min-w-0 items-center gap-1 truncate text-[11px] text-white/45">
                <span className="flex-none">{countryFlag || '--'}</span>
                <span className="truncate">{cityLabel || fallbackCityLabel}</span>
              </div>
            </div>
            <div className="inline-flex flex-none items-center gap-1 rounded-full border border-white/12 bg-white/5 px-1.5 py-[2px] text-[10px] leading-none text-white/70">
              <Hash size={10} />
              <span>{displayUnitCount.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'row') {
    const cellRenderers: Record<string, React.ReactNode> = {
      property: (
        <div className="flex h-full min-w-0 items-center gap-2">
          <div className="min-w-0 truncate font-medium text-white">{data.name}</div>
          {data.urls?.[0] && (
            <a
              href={data.urls[0]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/30 hover:text-white"
              onClick={stopEventPropagation}
            >
              <ExternalLink size={11} />
            </a>
          )}
        </div>
      ),
      owner: (
        <div className="flex h-full min-w-0 items-center gap-2 text-white/70">
          <div className="flex h-5 w-5 flex-none items-center justify-center overflow-hidden rounded border border-white/10 bg-white/5">
            {portfolio?.logoUrl ? (
              <img src={portfolio.logoUrl} alt={portfolio.name || data.portfolioName || 'Portfolio'} className="h-full w-full object-contain" />
            ) : (
              <Building size={10} className="text-white/30" />
            )}
          </div>
          <span className="min-w-0 truncate">{portfolio?.name || data.portfolioName || '-'}</span>
        </div>
      ),
      type: (
        <div className="flex h-full items-center">
          <span className="inline-flex max-w-full items-center truncate rounded-md bg-white/[0.06] px-2 py-0.5 text-[12px] capitalize text-white/75">
            {(data.type || 'Retail').replace(/_/g, ' ').toLowerCase()}
          </span>
        </div>
      ),
      managers: (
        <div className="flex h-full items-center gap-1 overflow-hidden" onClick={stopEventPropagation}>
          {data.leasingManagers?.map((manager) => (
            <PropertyManagerChip
              key={manager.id}
              manager={manager}
              onContextMenu={handleManagerContextMenu}
            />
          ))}
          <div className="pointer-events-none opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
            <UserSelector
              selectedMember={null}
              onSelect={handleAddLeasingManager}
              organisationId={data.portfolioOrganisationId || undefined}
            />
          </div>
        </div>
      ),
      units: (
        <div className="flex h-full items-center justify-end text-right text-white/90">
          {displayUnitCount.toLocaleString()}
        </div>
      ),
      occupancy: (
        <div className="flex h-full items-center justify-end text-right text-white/90">
          {data.occupancyRate ?? 0}%
        </div>
      ),
      vacant: (
        <div className="flex h-full items-center justify-end text-right text-white/90">
          {(data.vacantAreaSqm ?? 0).toLocaleString()} m2
        </div>
      ),
      actions: (
        <div className="flex h-full items-center justify-end gap-2">
          <EntityRowActions entityType="Property" entity={data} rowActions={rowActions} />
        </div>
      ),
    };

    const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));

    return (
      <div className={clsx(styles.gridRow, styles.bodyRow, 'group', className)} onClick={handleClick}>
        {activeColumns.map((column) => (
          <div
            key={column.id}
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
            style={resolveCellStyle(column)}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }

  return null;
});

PropertyRenderer.displayName = 'PropertyRenderer';
